/**
 * V103-4-5 / V103-10-6 — a menu as a DISCLOSURE (button + list of buttons),
 * not an ARIA menu: Tab moves through the choices, Esc and choosing close it
 * and return focus to the trigger, focus leaving closes it. `SplitButton`
 * pairs a default action with a caret that opens the other choices.
 */
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from '@aigovops/design-system';

export interface MenuChoice { label: string; onChoose: () => void; disabled?: boolean }

function useDisclosure() {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = (refocus: boolean) => { setOpen(false); if (refocus) trigger.current?.focus(); };
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); close(true); } };
    document.addEventListener('keydown', onKey);
    // First choice takes focus when the list opens.
    wrap.current?.querySelector<HTMLButtonElement>('[data-part="menu"] button:not([aria-disabled="true"])')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  const onBlur = (e: React.FocusEvent) => { if (open && !wrap.current?.contains(e.relatedTarget as Node | null)) setOpen(false); };
  return { open, setOpen, wrap, trigger, close, onBlur };
}

function MenuList({ id, choices, close, labelledBy }: { id: string; choices: MenuChoice[]; close: (refocus: boolean) => void; labelledBy: string }) {
  return (
    <span className="gp-cb-menu" data-part="menu" role="group" id={id} aria-labelledby={labelledBy}>
      {choices.map((c) => (
        <button key={c.label} type="button" aria-disabled={c.disabled || undefined}
          onClick={() => { if (c.disabled) return; close(true); c.onChoose(); }}>{c.label}</button>
      ))}
    </span>
  );
}

/** A labelled button that opens a list of choices ("Remove from boundary ▾"). */
export function DisclosureMenu({ label, ariaLabel, choices, variant = 'secondary' }: { label: ReactNode; ariaLabel?: string; choices: MenuChoice[]; variant?: 'secondary' | 'ghost' | 'primary' }) {
  const d = useDisclosure();
  const id = useId(), tid = useId();
  return (
    <span className="gp-cb-split" ref={d.wrap} onBlur={d.onBlur}>
      <Button ref={d.trigger} id={tid} compact variant={variant} aria-expanded={d.open} aria-controls={id} aria-label={ariaLabel} onClick={() => d.setOpen(!d.open)}>
        {label} <span aria-hidden="true">▾</span>
      </Button>
      {d.open ? <MenuList id={id} choices={choices} close={d.close} labelledBy={tid} /> : null}
    </span>
  );
}

/** Default action + caret for the alternatives. The caret's name says what it opens (label-in-name for the default). */
export function SplitButton({ label, ariaLabel, onClick, caretLabel, choices, variant = 'secondary', id }: {
  label: string; ariaLabel?: string; onClick: () => void; caretLabel: string; choices: MenuChoice[]; variant?: 'secondary' | 'primary'; id?: string;
}) {
  const d = useDisclosure();
  const mid = useId(), tid = useId();
  return (
    <span className="gp-cb-split" data-variant={variant} ref={d.wrap} onBlur={d.onBlur}>
      <Button id={id} compact variant={variant} aria-label={ariaLabel} onClick={onClick} data-part="main">{label}</Button>
      <Button ref={d.trigger} id={tid} compact variant={variant} data-part="caret" aria-expanded={d.open} aria-controls={mid} aria-label={caretLabel} onClick={() => d.setOpen(!d.open)}>
        <span aria-hidden="true">▾</span>
      </Button>
      {d.open ? <MenuList id={mid} choices={choices} close={d.close} labelledBy={tid} /> : null}
    </span>
  );
}
